import api from '../../../services/api';
import {
  GENERATE_PREVIEW,
  GET_OFFER_LIST,
  GET_PRODUCT_LIST,
  GET_PROMOTION,
  SAVE_PROMOTION,
} from '../../../constants/api';
import {
  EXPIRY_DATE,
  LAST_INSTALLATION_DATE,
  LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
  OR,
} from '../constants';
import React from 'react';
import { Typography } from '@mui/material';

export async function getPromotionData(id) {
  try {
    const response = await api.get(GET_PROMOTION(id));
    return response.data;
  } catch (error) {
    console.error(error);
  }
}

export async function getProductAndOfferList() {
  try {
    const [promotionResponse, offerResponse] = await Promise.all([
      api.get(`${GET_PRODUCT_LIST}?product_list`),
      api.post(`${GET_OFFER_LIST}?offer_list`),
    ]);
    return [promotionResponse.data, offerResponse.data];
  } catch (error) {
    console.error(error);
  }
}

export async function createPromotion(promotionData) {
  try {
    const response = await api.post(SAVE_PROMOTION, promotionData);
    return response;
  } catch (error) {
    return error.response.data;
  }
}

export async function updatePromotion(promotionData) {
  try {
    const response = await api.put(SAVE_PROMOTION, promotionData);
    return response;
  } catch (error) {
    return error.response.data;
  }
}

export async function generatePreview(promotionData) {
  try {
    const response = await api.post(GENERATE_PREVIEW, promotionData);
    return response;
  } catch (error) {
    return error.response.data;
  }
}

export function getExpiryDate(offer_expiry) {
  if (offer_expiry?.expiry_dates.length === 2) {
    return [
      {
        id: 31,
        value: offer_expiry?.expiry_dates[0],
        offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
      },
      {
        id: 32,
        value: offer_expiry?.expiry_dates[1],
        offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
      },
    ];
  } else {
    return [offer_expiry];
  }
}

const singleOfferWithSweetHead = (hasDiscount, hasSweetner, discount, finance) => {
  if (hasDiscount) {
    return hasSweetner ? [`${discount}¹`] : [`${discount}*`];
  } else {
    return hasSweetner ? [`${finance[0]}¹`] : [`${finance[0]}*`];
  }
};

const formatSweetenerText = (hasSweetener, hasMultipleOffers, sweetener) => {
  if (!hasSweetener) return '';

  const superscript = hasMultipleOffers ? '³' : '²';
  const sweetenerWithSuperscript = `${sweetener}${superscript}`;
  const includesPlus = sweetenerWithSuperscript.toLowerCase().includes('plus');

  return includesPlus ? sweetenerWithSuperscript : `PLUS ${sweetenerWithSuperscript}`;
};

const constructHeadlineParts = (
  flipValue,
  hasTwoFinances,
  hasDiscount,
  hasFinance,
  hasSweetener,
  DISCOUNT,
  FINANCE
) => {
  if (hasTwoFinances) {
    return flipValue ? [`${FINANCE[1]}¹`, `${FINANCE[0]}²`] : [`${FINANCE[0]}¹`, `${FINANCE[1]}²`];
  }

  if (hasDiscount && hasFinance) {
    return flipValue ? [`${FINANCE[0]}¹`, `${DISCOUNT}²`] : [`${DISCOUNT}¹`, `${FINANCE[0]}²`];
  }

  return singleOfferWithSweetHead(hasDiscount, hasSweetener, DISCOUNT, FINANCE);
};

const isValidHeadlineData = (data) => {
  return data !== undefined && data !== null && Object.keys(data).length > 0;
};

const isValidHeadline = (headline) => {
  return headline !== ' ' && headline.length > 0;
};

export function constructHeadline(data, flipValue = false, selectedConjunction = OR) {
  if (!isValidHeadlineData(data)) {
    return '[Please Select an HEADLINE]';
  }

  if (typeof data?.headline?.FINANCE === 'string') {
    data.headline.FINANCE = [data.headline.FINANCE];
  }

  const { DISCOUNT = '', FINANCE = [], SWEETENER = '' } = data.headline;
  const hasDiscount = DISCOUNT.length > 0;
  const hasSweetener = SWEETENER.length > 0;
  const hasFinance = FINANCE?.length > 0;
  const hasTwoFinances = FINANCE.length > 1;

  if (!hasDiscount && !hasFinance) {
    return '[Please Select an HEADLINE]';
  }

  const hasMultipleOffers = (hasDiscount && hasFinance) || hasTwoFinances;
  const conjunction = hasMultipleOffers ? selectedConjunction : '';
  const sweetenerText = formatSweetenerText(hasSweetener, hasMultipleOffers, SWEETENER);

  const parts = constructHeadlineParts(
    flipValue,
    hasTwoFinances,
    hasDiscount,
    hasFinance,
    hasSweetener,
    DISCOUNT,
    FINANCE
  );

  const headlineParts = parts.filter((part) => part.length).join(` ${conjunction} `);
  const headline = `${headlineParts} ${sweetenerText}`.trim();

  if (!isValidHeadline(headline)) {
    return '[Please Select an HEADLINE]';
  }

  return headline;
}

export function renderDisclaimerText(textArray) {
  return textArray.map((line, lineIndex) => {
    const uniqueKey = `disclaimer-${lineIndex}-${line?.substring(0, 20) || 'empty'}`;
    return (
      <React.Fragment key={uniqueKey}>
        {lineIndex !== 0 ? (
          <span style={{ background: '#d0443729' }} className="nonzero">
            {line}
          </span>
        ) : (
          <>
            {line}
            {line?.length ? <span dangerouslySetInnerHTML={{ __html: '&nbsp;' }} /> : ''}
          </>
        )}
      </React.Fragment>
    );
  });
}

// Helper to normalize FINANCE data to array format
function normalizeFinanceData(disclaimerData) {
  if (typeof disclaimerData?.FINANCE === 'object' && !Array?.isArray(disclaimerData?.FINANCE)) {
    disclaimerData.FINANCE = [disclaimerData?.FINANCE];
  }
}

// Helper to build all available keys from disclaimer data
function buildDisclaimerKeys(disclaimerData) {
  return [
    ...('DISCOUNT' in disclaimerData ? ['DISCOUNT'] : []),
    ...('FINANCE' in disclaimerData && Array.isArray(disclaimerData.FINANCE)
      ? disclaimerData.FINANCE.map((_, index) => `FINANCE${index}`)
      : []),
    ...Object.keys(disclaimerData).filter(
      (key) => key !== 'DISCOUNT' && !key.startsWith('FINANCE') && key !== 'SWEETENER'
    ),
    ...('SWEETENER' in disclaimerData ? ['SWEETENER'] : []),
  ];
}

// Helper to determine disclaimer keys order based on flip value and offer types
function determineDisclaimerKeysOrder(flipValue, hasDiscount, hasFinance, hasTwoFinances, keys) {
  if (!flipValue) {
    return keys;
  }

  if (hasTwoFinances) {
    return [`FINANCE1`, `FINANCE0`];
  }

  if (hasDiscount && hasFinance) {
    return [`FINANCE0`, `DISCOUNT`];
  }

  return keys;
}

// Helper to add SWEETENER if needed
function addSweetenerIfNeeded(disclaimerKeys, hasSweetner, flipValue) {
  const shouldAddSweetener = hasSweetner && !disclaimerKeys.includes('SWEETENER') && flipValue;
  return shouldAddSweetener ? [...disclaimerKeys, 'SWEETENER'] : disclaimerKeys;
}

// Helper to calculate superscript value
function calculateSuperscript(key, index, isSingleOfferType, totalKeys) {
  if (key === 'SWEETENER') {
    return totalKeys;
  }
  if (isSingleOfferType) {
    return '*';
  }
  return index + 1;
}

export function renderDisclaimerContent(disclaimerData, flipValue) {
  if (
    disclaimerData === undefined ||
    disclaimerData === null ||
    Object.keys(disclaimerData).length === 0
  )
    return null;

  normalizeFinanceData(disclaimerData);

  const { DISCOUNT = [], FINANCE = [], SWEETENER = [] } = disclaimerData;
  const hasDiscount = DISCOUNT.length > 0;
  const hasFinance = FINANCE.length > 0;
  const hasSweetner = SWEETENER.length > 0;
  const hasTwoFinances = FINANCE.length > 1;

  const keys = buildDisclaimerKeys(disclaimerData);
  const disclaimerKeys = determineDisclaimerKeysOrder(
    flipValue,
    hasDiscount,
    hasFinance,
    hasTwoFinances,
    keys
  );
  const updatedDisclaimerKeys = addSweetenerIfNeeded(disclaimerKeys, hasSweetner, flipValue);
  const isSingleOfferType = updatedDisclaimerKeys.length === 1;

  return updatedDisclaimerKeys.map((key1, index) => {
    const currentData = key1.includes('FINANCE')
      ? disclaimerData.FINANCE[key1.charAt(key1.length - 1)]
      : disclaimerData[key1];

    const superscriptValue = calculateSuperscript(
      key1,
      index,
      isSingleOfferType,
      updatedDisclaimerKeys.length
    );

    const uniqueKey = `disclaimer-content-${key1}-${index}`;

    return (
      <Typography variant="body1" component="p" key={uniqueKey}>
        <sup>{superscriptValue}</sup>
        {Object.keys(currentData).map((key2) => (
          <React.Fragment key={key2}>
            {Array.isArray(currentData[key2]) ? (
              renderDisclaimerText(currentData[key2])
            ) : (
              <>
                {Object.values(currentData[key2])
                  ?.flat()
                  ?.map((text) => (text ? text.trim() : ''))
                  .filter(Boolean)
                  .join(' ')}
              </>
            )}
          </React.Fragment>
        ))}
        {Object.keys(currentData).length ? <br /> : null}
      </Typography>
    );
  });
}
export function getErrorMessages(data) {
  return (
    <span
      dangerouslySetInnerHTML={{
        __html:
          data?.error_msg !== undefined
            ? data?.error_msg
            : JSON.stringify(data?.error_config?.errors),
      }}
    />
  );
}

export const renderNotesContent = (text, fromEditPromotions) => {
  return (
    <Typography
      variant="h6"
      component="h6"
      sx={{ fontWeight: 'bold', fontSize: 16, paddingBottom: fromEditPromotions ? 0 : 2 }}
    >
      {' '}
      NOTES: {text}
    </Typography>
  );
};

const capitalizeFirstLetter = (str) => str && str[0].toUpperCase() + str.slice(1);

export const renderSubHeadline = (subheadline, flipValue) => {
  const [firstSubHead, secondSubHead] = subheadline || ['', ''];
  return (
    <>
      <Typography variant="h4" sx={{ fontSize: '17px' }}>
        {flipValue
          ? !!secondSubHead && capitalizeFirstLetter(secondSubHead)
          : (!!firstSubHead && firstSubHead) || ''}
      </Typography>
      <Typography variant="h4" sx={{ fontSize: '17px' }}>
        {!flipValue ? !!secondSubHead && secondSubHead : (!!firstSubHead && firstSubHead) || ''}
      </Typography>
    </>
  );
};

export const isValidRestrictedStores = (restricted_stores = [], selectedStores = []) => {
  if (
    Array.isArray(restricted_stores) &&
    restricted_stores.length > 0 &&
    restricted_stores.every((store) => typeof store === 'number')
  ) {
    const selectedStoreId = selectedStores[0];
    const isSingleStoreMatch =
      selectedStores.length === 1 && restricted_stores.includes(selectedStoreId);
    const isExactMatch =
      selectedStores.length === restricted_stores.length &&
      selectedStores.every((store) => restricted_stores.includes(store));
    return isSingleStoreMatch || isExactMatch;
  }
  return true;
};

export const getOfferId = (data, headline_builder_id, customValues = {}) => {
  if (!Array.isArray(data) || data?.length === 0) {
    return null; // Handle case where data is not present
  }

  const item = data?.find((d) => d.id === headline_builder_id);

  if (!item) {
    return null; // Handle case where no matching id is found
  }

  // Check if the item has template_variables and if they have values to match
  if (Array.isArray(item?.template_variables) && item?.template_variables?.length > 0) {
    const templateVariables = item?.template_variables;

    // Prepare the array of template values to match with custom values
    const customValuesArray = templateVariables?.map((varName) => customValues[varName]);

    // Find the matching option based on the custom values
    const matchingOption = item?.options?.find((option) => {
      // Check if all template values match the custom values for that option
      const isMatch = option?.template_values?.every((value, index) => {
        return value === customValuesArray?.[index]?.toString(); // Compare values as strings
      });
      return isMatch;
    });

    // If a matching option is found, return the offer_id
    if (matchingOption) {
      return matchingOption?.offer_id || null;
    }

    return item?.options?.[0]?.offer_id || null;
  }

  // Default behavior if template_variables array is empty or undefined
  return item?.options?.[0]?.offer_id || null;
};

export const updatedPayload = (promotionData, financeOffersList) => {
  if (promotionData?.finance_offer?.length > 0) {
    const firstOffer = promotionData?.finance_offer?.[0];
    if (!firstOffer.offer_id && !firstOffer.custom && financeOffersList?.length > 0) {
      promotionData.finance_offer[0] = {
        ...firstOffer,
        offer_id: getOfferId(
          financeOffersList,
          firstOffer?.headline_builder_id,
          firstOffer?.custom_values
        ),
      };
    }

    if (promotionData.finance_offer.length === 2) {
      const secondOffer = promotionData?.finance_offer?.[1];
      if (!secondOffer.offer_id && !secondOffer.custom && financeOffersList?.length > 0) {
        promotionData.finance_offer[1] = {
          ...secondOffer,
          offer_id: getOfferId(
            financeOffersList,
            secondOffer?.headline_builder_id,
            secondOffer?.custom_values
          ),
        };
      }
    }
  }
  return promotionData;
};

// New utility functions for OfferExpiryDate component
export const getOfferExpiryDateValues = ({
  offerApplicable,
  promoExpiry,
  promotionData,
  defaultExpiryDate,
}) => {
  // Helper functions to get date values
  const getExpiryDateValue = () => {
    if (!offerApplicable) return null;
    if (Object.keys(promoExpiry)?.length && promoExpiry[EXPIRY_DATE]) {
      return promoExpiry[EXPIRY_DATE];
    }
    return null;
  };

  const getInstallationDateValue = () => {
    if (!offerApplicable) return null;
    if (Object.keys(promoExpiry)?.length && promoExpiry[LAST_INSTALLATION_DATE]) {
      return promoExpiry[LAST_INSTALLATION_DATE];
    }
    return null;
  };

  const getConsultationDateValue = () => {
    if (!offerApplicable) return null;
    if (
      Object.keys(promoExpiry)?.length &&
      promoExpiry[LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE]
    ) {
      return promoExpiry[LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE][0];
    }
    return null;
  };

  const getPurchaseDateValue = () => {
    if (!offerApplicable) return null;
    if (
      Object.keys(promoExpiry)?.length &&
      promoExpiry[LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE]
    ) {
      return promoExpiry[LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE][1];
    }
    return null;
  };

  const getMinDate = () => {
    if (promotionData?.status === 'ARCHIVED') {
      return new Date();
    }
    if (promotionData?.start_date) {
      return new Date(promotionData.start_date);
    }
    return defaultExpiryDate;
  };

  const getMaxDate = () => {
    if (promotionData?.status === 'ARCHIVED') {
      return new Date();
    }
    if (promotionData?.end_date) {
      return new Date(promotionData.end_date);
    }
    return defaultExpiryDate;
  };

  return {
    expiryDateValue: getExpiryDateValue(),
    installationDateValue: getInstallationDateValue(),
    consultationDateValue: getConsultationDateValue(),
    purchaseDateValue: getPurchaseDateValue(),
    minDate: getMinDate(),
    maxDate: getMaxDate(),
  };
};
